import { ResponsiveHeroImage } from "@quintype/components";
import get from "lodash/get";
import PropTypes from "prop-types";
import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";

import { clientWidth, isExternalStory, getStoryUrl, removeHtmlTags } from "../../../utils/utils";
import { useStateValue } from "../../SharedContext";
import { FallbackImage } from "../FallbackImage";
import { HyperLink } from "../Hyperlink";
import { SvgIconHandler } from "../../../../components/atoms/SvgIconHandler";
import LiveButton from "../../../../components/atoms/live-button";
import { YTDialog } from "./dialog";

import "./yt-modal.m.css";

export const YTModal = ({
  children,
  story,
  FullBleed,
  aspectRatio,
  defaultWidth,
  widths,
  isHorizontal,
  isHorizontalMobile,
  isHorizontalWithImageLast,
  defaultFallback,
  isStoryPageImage,
  showIcon,
  isRecommended = false,
  isYTPageHeadline = false,
}) => {
  const { fallbackImageUrl = "" } = useStateValue() || {};
  const [isDesktop, setIsDesktop] = useState(false);
  const [windowPresent, setWindowPresent] = useState(false);

  const cdnHost = useSelector((state) => get(state, ["qt", "config", "cdn-image"]));

  const showImagePlaceholder = useSelector((state) => get(state, ["qt", "config", "showPlaceholder"]));
  const getPlaceholderStyleName = showImagePlaceholder ? "placeholder" : "";

  const ytId = useSelector((state) => get(state, ["qt", "data", "ytid"], false));
  const fullBleed = FullBleed ? "" : "with-padding";

  let heroImageClass = "hero-image";
  if (isHorizontal) heroImageClass = "right-padding";
  else if (isHorizontalWithImageLast) heroImageClass = "left-padding";

  const heroImageMobClasses = isHorizontalMobile ? "right-padding-mob" : "";

  const getPadding = (aspectRatio) => {
    const [height, width] = aspectRatio;
    if (height > 0 && width > 0) {
      const padding = (width / height) * 100;
      return padding;
    }
    return 0;
  };

  const [mobileAspectRatio, desktopAspectRatio = mobileAspectRatio] = aspectRatio;
  const isMobile = clientWidth("mobile");

  const imageAspectRatio = isMobile ? mobileAspectRatio : desktopAspectRatio;
  const imagePadding = isMobile ? getPadding(mobileAspectRatio) : getPadding(desktopAspectRatio);
  const slug =
    get(story, ["hero-image-s3-key"]) ||
    get(story, ["alternative", "home", "default", "hero-image", "hero-image-s3-key"]);
  const hyperLink = get(story, ["hero-image-hyperlink"], "");
  const imageCaption = get(story, ["hero-image-caption"], "");
  const altText = get(story, ["hero-image-alt-text"], "");
  const imageAltText = altText
    ? removeHtmlTags(altText)
    : imageCaption
    ? removeHtmlTags(imageCaption)
    : get(story, ["headline"], "");

  const getIcon = (story) => {
    const svgDimensions = isDesktop ? "24" : "20";
    switch (story?.["story-template"]) {
      case "video":
        return (
          <div styleName="video-icon">
            <SvgIconHandler
              type="story-play"
              width={svgDimensions}
              height={svgDimensions}
              viewBox={`0 0 ${svgDimensions} ${svgDimensions}`}
            />
          </div>
        );

      case "photo":
        return (
          <div styleName="photo-icon">
            <SvgIconHandler
              type="image"
              width={svgDimensions}
              height={svgDimensions}
              viewBox={`0 0 ${svgDimensions} ${svgDimensions}`}
            />
          </div>
        );
      case "visual-story":
        return (
          <div styleName="story-type-tag">
            <SvgIconHandler type="visual-story" width={24} height={24} viewBox="0 0 24 24" />
          </div>
        );
      case "live-blog":
        return <LiveButton story={story} isRecommended={isRecommended} />;
      default:
        return (
          <div styleName="story-type-tag">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="12" fill="white" />
              <path d="M9.69238 8L15.8462 11.6923L9.69238 15.3846V8Z" fill="black" />
            </svg>
          </div>
        );
    }
  };

  useEffect(() => {
    if (window.innerWidth >= 1200) {
      setIsDesktop(true);
    }
    if (typeof window !== "undefined") {
      setWindowPresent(true);
    }
  }, []);

  const dialogRef = useRef(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isManualClicked, setIsManualClicked] = useState(true);

  const handleClick = (e) => {
    e.preventDefault(); // Prevents the browser from following the link
    const href = e.currentTarget.getAttribute("href");
    window.history.pushState({ modalOpen: true }, "", href);
    setIsDialogOpen(true);
  };

  useEffect(() => {
    if (isDialogOpen) {
      document.body.style.overflow = "hidden";
    }

    if (isDialogOpen && dialogRef.current) {
      dialogRef.current.showModal();
    }

    return () => {
      document.body.style.overflow = ""; // cleanup on unmount
    };
  }, [isDialogOpen]);

  const handleContextMenu = (event) => {
    event.preventDefault();
  };

  const closeDialog = () => {
    if (dialogRef.current) {
      dialogRef.current.close();
      setIsDialogOpen(false);
      document.body.style.overflow = ""; // <-- Ensure this runs always
      window.history.pushState({ modalOpen: false }, "", "/short-videos");
      const heading = document.querySelector('h1');
      heading.textContent = 'Fortune India Short Videos';
    }
  };

  // Optional: close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeDialog();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const storyYtId = get(story, ["video_id"], false);

  // This opens the modal only once if ytId matches URL param
  useEffect(() => {
    if (ytId === storyYtId && isYTPageHeadline) {
      setIsManualClicked(false);
      setIsDialogOpen(true);
    }
  }, [ytId, storyYtId]);

  useEffect(() => {
    const onPopState = (event) => {
      closeDialog();
      if (event.state && event.state.dialogOpen) {
        // This popstate is specifically for our dialog
        closeDialog();
        // Optionally push back an empty state to keep the dialog from reopening on forward navigation
        window.history.pushState({}, "", window.location.pathname);
      } else {
        // Handle other popstate events, e.g., actual page navigation
        closeDialog(); // or your default back behavior
      }
    };

    window.addEventListener("popstate", onPopState);

    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  const fallbackImage = () => {
    if (!slug) {
      if (fallbackImageUrl && !isStoryPageImage) {
        return (
          <figure
            styleName={`image ${getPlaceholderStyleName}`}
            className="arr--responsive-hero-image"
            style={{
              paddingTop: imagePadding + `%`,
            }}
          >
            <img className="qt-image" data-src={`${fallbackImageUrl}`} alt="image-fallback"></img>
          </figure>
        );
      }

      return (
        <>
          {defaultFallback ? (
            <div
              styleName={`image ${getPlaceholderStyleName}`}
              className="arr--fallback-hero-image"
              data-test-id="arr--fallback-hero-image"
              style={{
                paddingTop: imagePadding + `%`,
              }}
            >
              {showIcon && getIcon(story)}
              <FallbackImage />
            </div>
          ) : null}
        </>
      );
    }

    return (
      <>
        <figure
          styleName={`image ${getPlaceholderStyleName}`}
          className="arr--responsive-hero-image"
          style={{ paddingTop: imagePadding + `%` }}
        >
          {showIcon && getIcon(story)}
          {!windowPresent ? (
            <img
              src={`https://${cdnHost}/${story?.["hero-image-s3-key"]}?w=640&auto=format,compress&fit=max&q=70`}
              srcSet={`https://${cdnHost}/${story?.["hero-image-s3-key"]}?w=480&auto=format,compress&fit=max&q=70 480w, https://${cdnHost}/${story?.["hero-image-s3-key"]}?w=640&auto=format,compress&fit=max&q=70 768w`}
              sizes="(max-width: 767px) 480px, (min-width: 768px) 768px"
              alt={story["hero-image-alt-text"] || story?.headline}
              style={{ width: "100%" }}
            />
          ) : (
            <ResponsiveHeroImage
              story={story}
              title={imageAltText}
              aspectRatio={imageAspectRatio}
              defaultWidth={defaultWidth}
              widths={widths}
              imgParams={{ auto: ["format", "compress"], fit: "max", q: 70 }}
              alt={imageAltText}
              shouldDecodeImage={true}
            />
          )}
        </figure>
        {isStoryPageImage && hyperLink && <HyperLink hyperLink={hyperLink} />}
      </>
    );
  };

  return (
    <>
      {isYTPageHeadline ? (
        <>
          <a href={story.slug} onClick={handleClick} onContextMenu={handleContextMenu} rel="nofollow">
            {children}
          </a>
          {isDialogOpen && (
            <YTDialog story={story} closeDialog={closeDialog} dialogRef={dialogRef} isDialogOpen={isDialogOpen} isManualClicked={isManualClicked} />
          )}
        </>
      ) : (
        <>
          <div
            className="arr--hero-image"
            data-test-id="arr--hero-image"
            styleName={`${heroImageClass} ${fullBleed} ${heroImageMobClasses}`}
          >
            <a href={story.slug} onClick={handleClick} onContextMenu={handleContextMenu} rel="nofollow">
              {fallbackImage()}
            </a>
            {isDialogOpen && (
              <YTDialog story={story} closeDialog={closeDialog} dialogRef={dialogRef} isDialogOpen={isDialogOpen} isManualClicked={isManualClicked} />
            )}
          </div>
        </>
      )}
    </>
  );
};

YTModal.propTypes = {
  /** The Story Object from the API response */
  story: PropTypes.object.isRequired,
  /** Removes the Hero Image padding for the image to bleed over the margin */
  FullBleed: PropTypes.bool,
  /** The default width for the image, in case the browser does not support srcset */
  defaultWidth: PropTypes.number,
  /** The list of available widths */
  widths: PropTypes.array,
  // fatch the correct hero image
  aspectRatio: PropTypes.array,

  //  Hints for the browser to choose the best size
  /** This prop is responsible for adding padding-right of the image and remove padding bottom of the image  */
  isHorizontal: PropTypes.bool,
  /** This prop is responsible for adding padding-bottom in desktop and padding-right in mobile */
  isHorizontalMobile: PropTypes.bool,
  // this prop is responsive for rendering fallback image on story page
  defaultFallback: PropTypes.bool,
  isHorizontalWithImageLast: PropTypes.bool,
  isStoryPageImage: PropTypes.bool,
  showIcon: PropTypes.bool,
  isYTPageHeadline: PropTypes.bool,
};

YTModal.defaultProps = {
  FullBleed: true,
  aspectRatio: [
    [1, 1],
    [16, 9],
  ],
  defaultWidth: 480,
  widths: [250, 480, 640],
  isHorizontal: false,
  isHorizontalMobile: false,
  defaultFallback: true,
  isStoryPageImage: false,
  isHorizontalWithImageLast: false,
  showIcon: true,
};
